import React from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import { faSpinnerThird, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { escapeRegExp } from "lodash";

import type { SimpleContact } from "~/invite";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsPage, dispatchAnalyticsTrack } from "~/lib/segment";
import { NON_BREAKING_HYPHEN as NB_HYPHEN } from "~/lib/unicode";
import { listFormat, reduceEdgesToNodes } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import managerAvatars from "./images/manager-avatars.png";
import { JourneyContext } from "./JourneyContext";

import type {
	ManagerPageQuery$data as QueryResponse,
	ManagerPageQuery as QueryType,
} from "~/__generated__/ManagerPageQuery.graphql";
import type {
	InviteUserInput,
	ManagerPageSetManagerMutation as MutationType,
} from "~/__generated__/ManagerPageSetManagerMutation.graphql";

import stylesOnboarding from "~/onboarding/css/Onboarding.module.css";

import styles from "./css/ManagerPage.module.css";

export const MANAGER_PATH = "/o/manager/";

const ManagerPageSetManagerMutation = graphql`
	mutation ManagerPageSetManagerMutation($input: InviteUsersMutationInput!) {
		inviteUsers(input: $input) {
			invitees {
				errors {
					field
					message
				}
			}
		}
	}
`;

type Domain = QueryResponse["viewer"]["account"]["domains"][0];

type ManagerInputProps = React.HTMLProps<HTMLInputElement> &
	React.InputHTMLAttributes<HTMLInputElement> & {
		manager: SimpleContact;
		selected: boolean;
		inputError: string | undefined;
		validDomains: string[];
		setInputError: (error: string | undefined) => void;
		onSelectManager: (manager: SimpleContact) => void;
		onInputChange: (email: string) => void;
	};

function ManagerInput({
	manager,
	selected,
	validDomains,
	inputError,
	setInputError,
	onSelectManager,
	onInputChange,
}: ManagerInputProps) {
	const [hasBlurred, setHasBlurred] = React.useState(false);

	const domainRegex = React.useMemo(() => {
		const pattern = `^[^@]+@(${validDomains.map(escapeRegExp).join("|")})$`;
		return validDomains.length > 0 ? new RegExp(pattern, "i") : new RegExp("^[^@]+@[^@]+\\.[a-zA-Z]+$");
	}, [validDomains]);

	const domainList = React.useMemo(
		() =>
			listFormat(
				validDomains.map(domain => `@${domain}`),
				{ sep: ", ", last: " or " },
			).join(""),
		[validDomains],
	);

	const showErrors = React.useMemo(() => {
		return hasBlurred && selected;
	}, [hasBlurred, selected]);

	const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.code !== "Enter") return;
		e.currentTarget.blur();
	}, []);

	const onChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onInputChange(e.target.value);
			if (!e.target.validity.valid) {
				setInputError(`Invalid email address. Please use an ${domainList} email address`);
			} else {
				setInputError(undefined);
			}
		},
		[onInputChange, setInputError, domainList],
	);

	const onBlur = React.useCallback(() => {
		setHasBlurred(true);
	}, []);

	return (
		<>
			<div className={styles.inputContainer}>
				<span className={styles.profile}>
					<span className={styles.icon}>
						<FontAwesomeIcon icon={faUser} />
					</span>
				</span>
				<input
					type="email"
					onClick={() => onSelectManager(manager)}
					className={cx(styles.input, { [styles.selected]: selected, [styles.showErrors]: showErrors })}
					value={manager.email}
					onChange={onChange}
					onKeyDown={onKeyDown}
					onBlur={onBlur}
					pattern={domainRegex.source}
					placeholder="Add your manager by email"
				/>
				{selected ? (
					<span className={styles.radioSelected} />
				) : manager.email ? (
					<span className={styles.radio} />
				) : null}
			</div>
			{inputError && showErrors && <small className={styles.inputError}>{inputError}</small>}
		</>
	);
}

type ManagerSuggestionProps = {
	manager: SimpleContact;
	selected: boolean;
	onSelectManager: (manager: SimpleContact) => void;
};

function ManagerSuggestion({ manager, selected, onSelectManager }: ManagerSuggestionProps) {
	return (
		<div className={styles.suggestionContainer} onClick={() => onSelectManager(manager)}>
			<span className={styles.profile}>
				<Avatar width="24px" user={manager} />
			</span>
			<p className={cx(styles.suggestion, { [styles.selected]: selected })}>{manager.email}</p>
			{selected ? <span className={styles.radioSelected} /> : <span className={styles.radio} />}
		</div>
	);
}

export function ManagerPage() {
	const { nextStep, user } = React.useContext(JourneyContext);

	const { viewer, contacts } = useLazyLoadQuery<QueryType>(
		graphql`
			query ManagerPageQuery($doesNotReportUnder: ID) {
				contacts(
					first: 3
					ranked: true
					excludeUsers: false
					excludeConfigured: false
					excludeGroups: true
					excludeExternal: true
					excludeInactive: true
					doesNotReportUnder: $doesNotReportUnder
				) {
					edges {
						node {
							...ContactSearch_contact @relay(mask: false)
						}
					}
				}
				viewer {
					account {
						domains {
							domain
							aliases
						}
					}
				}
			}
		`,
		{
			doesNotReportUnder: user?.id,
		},
		{ fetchPolicy: "store-or-network" },
	);

	const suggestedManagers: SimpleContact[] = React.useMemo(() => reduceEdgesToNodes(contacts?.edges), [contacts]);

	const domains = React.useMemo(() => {
		return (viewer.account.domains as Domain[]).reduce((acc: string[], domain: Domain) => {
			return domain ? [...acc, domain.domain, ...domain.aliases] : acc;
		}, []);
	}, [viewer.account.domains]);

	const [isLoading, setIsLoading] = React.useState(false);
	const [selectedManagerId, setSelectedManagerId] = React.useState<string | undefined>();
	const [customManager, setCustomManager] = React.useState<SimpleContact>({
		id: "custom",
		email: "",
		fullName: "",
		user: null,
	});
	const [inputError, setInputError] = React.useState<string | undefined>(undefined);

	React.useEffect(() => {
		dispatchAnalyticsPage("Onboarding", "Manager", {
			numSuggestions: suggestedManagers.length,
		});
	}, [suggestedManagers]);

	const onSelectManager = React.useCallback((manager: SimpleContact) => {
		setSelectedManagerId(manager.id);
	}, []);

	const onInputChange = React.useCallback((email: string) => {
		setCustomManager(manager => {
			return { ...manager, email };
		});
	}, []);

	const onSetManager = React.useCallback(async () => {
		if (!selectedManagerId) return;
		const selectedManager = suggestedManagers.find(manager => manager.id === selectedManagerId) ?? customManager;

		const invitations: InviteUserInput[] = [
			{
				email: selectedManager.email,
				fullName: selectedManager.fullName ? selectedManager.fullName : selectedManager.email,
				sendInviteEmail: true,
				relationship: "MANAGER",
			},
		];
		setIsLoading(true);
		const captchaToken = await recaptcha("onboarding_manager");

		commitMutation<MutationType>(environment, {
			mutation: ManagerPageSetManagerMutation,
			variables: {
				input: {
					invitations,
					captchaToken,
					// eslint-disable-next-line camelcase
					analytics: JSON.stringify({ ux_location: "onboarding__manager_page" }),
				},
			},
			onCompleted: () => {
				dispatchAnalyticsTrack("Onboarding Manager Set", {
					feature: "Onboarding",
					usedSuggestedManager: Boolean(suggestedManagers.find(manager => manager.id === selectedManagerId)),
				});
				setIsLoading(false);
				nextStep();
			},
		});
	}, [selectedManagerId, nextStep, suggestedManagers, customManager]);

	const skipManager = React.useCallback(() => {
		dispatchAnalyticsTrack("Onboarding Manager Skipped", {
			feature: "Onboarding",
			numSuggestions: suggestedManagers.length,
		});
		nextStep();
	}, [nextStep, suggestedManagers]);

	return (
		<>
			<img src={managerAvatars} className={styles.avatars} />
			<div className={stylesOnboarding.title}>Add your manager</div>
			<h2 className={cx(styles.subtitle, stylesOnboarding.subtitle)}>
				Set your manager in Fellow to get suggested topics for your 1{NB_HYPHEN}on{NB_HYPHEN}1s.
			</h2>
			<div className={styles.content}>
				{suggestedManagers.map(manager => (
					<ManagerSuggestion
						key={manager.id}
						manager={manager}
						selected={selectedManagerId === manager.id}
						onSelectManager={onSelectManager}
					/>
				))}
				<ManagerInput
					manager={customManager}
					selected={selectedManagerId === customManager.id}
					inputError={inputError}
					validDomains={domains}
					setInputError={setInputError}
					onInputChange={onInputChange}
					onSelectManager={onSelectManager}
				/>
				<small className={styles.subheading}>New teammates will be invited to join Fellow.</small>
			</div>
			<div className={stylesOnboarding.footer}>
				<Button onClick={skipManager}>Not now</Button>
				<Button
					variant="primary"
					onClick={onSetManager}
					disabled={
						!selectedManagerId ||
						(selectedManagerId === "custom" && !!inputError) ||
						(selectedManagerId === "custom" && customManager.email === "") ||
						isLoading
					}
				>
					Set as manager {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}
				</Button>
				<div className={stylesOnboarding.submessage}>
					<div>You can always change this later in your settings.</div>
				</div>
			</div>
		</>
	);
}
